import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Users,
  Clock,
  Building,
  MapPinIcon,
  PhoneCall,
  User,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { branchsAPI } from '@/services/branchs';
import { format } from 'date-fns';
import { getStatusBadge } from '@/utils/statusUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal';

export function BranchDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: deleteBranch, isPending: isDeleting } = useMutation({
    mutationFn: () => branchsAPI.deleteBranch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch deleted successfully');
      navigate('/branch-management');
    },
    onError: (error) => {
      console.error('Error deleting branch:', error);
      toast.error('Failed to delete branch');
    },
  });

  const {
    data: branch,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['branch', id],
    queryFn: () => branchsAPI.getBranchById(id as string),
    enabled: !!id,
  });

  if (!id) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Branch not found</p>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const getMemberRoleBadge = (role: string) => {
    const roleConfig = {
      owner: { label: 'Owner', className: 'bg-purple-100 text-purple-800' },
      manager: { label: 'Manager', className: 'bg-blue-100 text-blue-800' },
      staff: { label: 'Staff', className: 'bg-gray-100 text-gray-800' },
    };

    const config = roleConfig[role as keyof typeof roleConfig] || {
      label: role,
      className: 'bg-gray-100 text-gray-800',
    };

    return (
      <Badge className={`text-xs ${config.className}`}>{config.label}</Badge>
    );
  };

  if (isLoading) {
    return (
      <div className=" p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="h-64 bg-gray-200 rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !branch) {
    return (
      <div className=" p-6">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900">
            Branch not found
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            The branch you're looking for doesn't exist or you don't have access
            to it.
          </p>
          <div className="mt-6">
            <Button onClick={() => navigate('/branch-management')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Branches
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            className="flex items-center"
            onClick={() => navigate('/branch-management')}
          >
            <ArrowLeft className="h-4 w-4 md:mr-2" />
            <span className="">Back to Branches</span>
          </Button>
          <div className="h-6 w-px bg-border hidden sm:block" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <h1 className="text-2xl font-semibold">{branch.name}</h1>
            <div className="flex items-center gap-2">
              {getStatusBadge(branch?.isActive ? 'active' : 'inactive')}
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200"
              >
                {branch.companyName}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="flex items-center flex-1 md:flex-none"
            onClick={() => navigate(`/branch-management/edit/${branch.id}`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Branch
          </Button>
          <Button
            variant="destructive"
            className="flex items-center flex-1 md:flex-none"
            onClick={() => setIsDeleteModalOpen(true)}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>

      {/* Branch Overview */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Branch Images */}
        <div className="space-y-4">
          <div className="aspect-video  w-full rounded-lg overflow-hidden bg-muted">
            {branch.imageUrls && branch.imageUrls.length > 0 ? (
              <ImageWithFallback
                src={branch.imageUrls[0]}
                alt={branch.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                <Building className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map((_, index) => (
              <div
                key={index}
                className=" rounded-lg h-[100px] overflow-hidden bg-muted/50"
              >
                {branch.imageUrls && branch.imageUrls[index + 1] ? (
                  <ImageWithFallback
                    src={branch.imageUrls[index + 1]}
                    alt={`${branch.name} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <span className="text-xs text-gray-400">No image</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Branch Details */}
        <div className="md:col-span-1 space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="py-3 sm:py-4">
              <CardTitle className="text-base sm:text-lg flex items-center">
                <Building className="h-5 w-5 mr-2" />
                Branch Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 text-sm">
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <MapPinIcon className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm sm:text-base">Address</p>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      {branch.address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm sm:text-base">Phone</p>
                    <p className="text-muted-foreground text-xs sm:text-sm">
                      {branch.phoneNumber}
                    </p>
                  </div>
                </div>
                {branch.members.length > 0 && (
                  <div className="flex items-start space-x-2 sm:space-x-3 pt-2">
                    <Users className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="font-medium mb-1 text-sm sm:text-base">
                        Team Members
                      </p>
                      {branch.members.map((member) => (
                        <div
                          key={member.userId}
                          className="flex items-center space-x-2 py-1 text-xs sm:text-sm"
                        >
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">
                              {member.firstName} {member.lastName}
                            </span>
                            {getMemberRoleBadge(member.role)}
                          </div>
                          <a
                            href={`tel:${member.phoneNumber}`}
                            className="text-xs sm:text-sm text-blue-600 hover:underline flex items-center"
                          >
                            <PhoneCall className="h-3 w-3 mr-1" />
                            {member.phoneNumber}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3 sm:py-4">
              <CardTitle className="text-base sm:text-lg flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Branch Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 sm:space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground text-xs sm:text-sm">
                  Company
                </span>
                <span className="font-medium text-sm sm:text-base">
                  {branch.companyName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-xs sm:text-sm">
                  Status
                </span>
                <span className="font-medium text-sm sm:text-base">
                  {getStatusBadge(branch.isActive ? 'active' : 'inactive')}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground text-xs sm:text-sm">
                  Created At
                </span>
                <span className="font-medium text-sm sm:text-base">
                  {formatDate(branch.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground text-xs sm:text-sm">
                  Last Updated
                </span>
                <span className="font-medium text-sm sm:text-base">
                  {formatDate(branch.updatedAt)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Members */}
      {branch.members.length > 0 && (
        <Card className="gap-2 md:gap-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Users className="h-5 w-5 mr-2" />
              <span className="text-base sm:text-lg"> Team Members</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {branch.members.map((member) => (
                <div
                  key={member.userId}
                  className="flex items-center space-x-4 p-3 border rounded-lg"
                >
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <User className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="font-medium truncate">
                        {member.firstName} {member.lastName}
                      </p>
                      {getMemberRoleBadge(member.role)}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <a
                        href={`tel:${member.phoneNumber}`}
                        className="hover:text-blue-600 hover:underline flex items-center"
                      >
                        <PhoneCall className="h-3 w-3 mr-1" />
                        {member.phoneNumber}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Branch Images Gallery */}
      {/* {branch.imageUrls && branch.imageUrls.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <ImageWithFallback
                src="/images/gallery-icon.svg"
                alt=""
                className="h-5 w-5 mr-2"
                width={20}
                height={20}
              />
              Branch Gallery
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {branch.imageUrls.map((url, index) => (
                <div
                  key={index}
                  className="aspect-square rounded-lg overflow-hidden border"
                >
                  <ImageWithFallback
                    src={url}
                    alt={`${branch.name} image ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                    width={200}
                    height={200}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )} */}

      {/* Performance Metrics */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center">
              <DollarSign className="h-4 w-4 mr-2" />
              Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className={`text-2xl font-bold ${getPerformanceColor()}`}>
                ${branch?.monthlyRevenue?.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">
                {getPerformancePercentage()}% of target
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center">
              <Target className="h-4 w-4 mr-2" />
              Monthly Target
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-2xl font-bold">
                ${branch?.monthlyTarget?.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">
                Set for this month
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center">
              <Star className="h-4 w-4 mr-2" />
              Customer Rating
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-2xl font-bold">
                {branch?.customerSatisfaction}/5.0
              </p>
              <p className="text-sm text-muted-foreground">Average rating</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Staff Count
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-2xl font-bold">{branch?.staff}</p>
              <p className="text-sm text-muted-foreground">Active employees</p>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Staff & Management */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Award className="h-5 w-5 mr-2" />
              Branch Manager
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-lg">{branch?.manager}</p>
                <p className="text-muted-foreground">Branch Manager</p>
                <p className="text-sm text-muted-foreground">{branch?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Today's Sales</span>
                <span className="font-medium">$3,247</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Weekly Performance</span>
                <span className="font-medium text-green-600">+12%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Inventory Items</span>
                <span className="font-medium">1,247</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Last Inventory Update</span>
                <span className="font-medium">2 hours ago</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={() => deleteBranch()}
        title="Delete Branch"
        description={
          <span>
            Are you sure you want to delete{' '}
            <span className="font-semibold">{branch?.name}</span>? This action
            cannot be undone.
          </span>
        }
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        isLoading={isDeleting}
        confirmButtonClass="bg-destructive hover:bg-destructive/90"
      />
    </div>
  );
}
